import { DesktopInput, DEFAULT_KEY_MAP, KeyMap, WheelConfig, DEFAULT_WHEEL_CONFIG, WheelMappingItem } from '../input/DesktopInput';
import { AudioManager } from '../core/AudioManager';
import { MirrorsAndCameras, DriverSettings } from '../vehicle/MirrorsAndCameras';
import { SettingsSyncService } from '../core/SettingsSync';
import { MobileTouchHUD, MobileSteeringMode, MobileHandedness, MobileButtonScale } from '../input/MobileTouchHUD';

export interface UserProfileSettings {
  driverName: string;
  preferredTransmission: 'automatic' | 'manual';
  useGyro: boolean;
  examinerVoiceEnabled: boolean;
  mobileSteerMode?: MobileSteeringMode;
  mobileHandedness?: MobileHandedness;
  mobileButtonScale?: MobileButtonScale;
  mobileHaptic?: boolean;
}

export class SettingsModal {
  public container: HTMLElement;
  private listeningActionKey: string | null = null;
  private keybindButtons: Map<string, HTMLButtonElement> = new Map();
  private onCloseCallback: (() => void) | null = null;
  private onOpenCockpitAdjusterCallback: (() => void) | null = null;
  private onToggleTalkBackCallback: ((enabled: boolean) => void) | null = null;
  private pollRafId: number | null = null;
  private calibratingTarget: string | null = null;
  private baselineAxes: number[] = [];
  private baselineButtons: number[] = [];
  private calibrationStatusElem: HTMLElement | null = null;

  private profile: UserProfileSettings = {
    driverName: 'Fahrschüler',
    preferredTransmission: 'automatic',
    useGyro: false,
    examinerVoiceEnabled: false,
    mobileSteerMode: 'arrows',
    mobileHandedness: 'right',
    mobileButtonScale: 'normal',
    mobileHaptic: true,
  };

  constructor(
    private desktopInput: DesktopInput,
    private audioManager: AudioManager,
    private mirrorsAndCameras?: MirrorsAndCameras,
    private mobileHUD?: MobileTouchHUD
  ) {
    this.loadProfile();
    this.container = document.createElement('div');
    this.container.id = 'settings-modal';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(9, 13, 22, 0.92);
      backdrop-filter: blur(14px);
      z-index: 60;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 16px;
      pointer-events: auto;
    `;

    this.buildUI();
    this.setupKeyCapture();
  }

  private loadProfile() {
    try {
      const s = SettingsSyncService.getInstance().getSettings();
      this.profile = {
        driverName: s.driverName || 'Fahrschüler',
        preferredTransmission: s.preferredTransmission || 'automatic',
        useGyro: s.useGyro ?? false,
        examinerVoiceEnabled: s.examinerVoiceEnabled ?? false,
        mobileSteerMode: 'arrows',
        mobileHandedness: 'right',
        mobileButtonScale: 'normal',
        mobileHaptic: true,
      };

      if (typeof localStorage !== 'undefined') {
        const mode = localStorage.getItem('fahrschule_mobile_steer_mode') as MobileSteeringMode;
        if (mode === 'arrows' || mode === 'wheel' || mode === 'gyro') {
          this.profile.mobileSteerMode = mode;
        }
        const hand = localStorage.getItem('fahrschule_mobile_handedness') as MobileHandedness;
        if (hand === 'right' || hand === 'left') {
          this.profile.mobileHandedness = hand;
        }
        const scale = localStorage.getItem('fahrschule_mobile_btn_scale') as MobileButtonScale;
        if (scale === 'normal' || scale === 'large') {
          this.profile.mobileButtonScale = scale;
        }
        const haptic = localStorage.getItem('fahrschule_mobile_haptic');
        if (haptic !== null) {
          this.profile.mobileHaptic = haptic === 'true';
        }
      }
    } catch {
      // fallback
    }
  }

  private saveProfile() {
    try {
      SettingsSyncService.getInstance().updateSettings({
        driverName: this.profile.driverName,
        preferredTransmission: this.profile.preferredTransmission,
        useGyro: this.profile.useGyro,
        examinerVoiceEnabled: this.profile.examinerVoiceEnabled,
      });

      if (typeof localStorage !== 'undefined') {
        if (this.profile.mobileSteerMode) {
          localStorage.setItem('fahrschule_mobile_steer_mode', this.profile.mobileSteerMode);
        }
        if (this.profile.mobileHandedness) {
          localStorage.setItem('fahrschule_mobile_handedness', this.profile.mobileHandedness);
        }
        if (this.profile.mobileButtonScale) {
          localStorage.setItem('fahrschule_mobile_btn_scale', this.profile.mobileButtonScale);
        }
        if (this.profile.mobileHaptic !== undefined) {
          localStorage.setItem('fahrschule_mobile_haptic', String(this.profile.mobileHaptic));
        }
      }
    } catch {
      // ignore
    }
  }

  private buildUI() {
    this.container.innerHTML = '';
    const card = document.createElement('div');
    card.id = 'settings-card';
    card.style.cssText = `
      background: #0f172a;
      border: 1px solid #3b82f6;
      border-radius: 20px;
      width: min(680px, 95vw);
      max-height: 94vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding: clamp(14px, 3.5vw, 26px);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.85);
      display: flex;
      flex-direction: column;
      gap: 18px;
      box-sizing: border-box;
    `;

    // 1. Header
    const header = document.createElement('div');
    header.style.cssText = `display:flex;justify-content:space-between;align-items:center;`;
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:10px;">
        <span style="font-size:24px;">⚙️</span>
        <h2 style="font-size:clamp(16px, 3vw, 20px);font-weight:800;color:#f8fafc;margin:0;">Einstellungen & Tastenbelegung</h2>
      </div>
    `;
    const btnCloseTop = document.createElement('button');
    btnCloseTop.innerText = '✕';
    btnCloseTop.title = 'Schließen';
    btnCloseTop.style.cssText = `
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #f8fafc;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      font-size: 18px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      box-sizing: border-box;
    `;
    btnCloseTop.addEventListener('click', () => this.close());
    header.appendChild(btnCloseTop);
    card.appendChild(header);

    // 2. Profile Section
    const profileSec = document.createElement('div');
    profileSec.innerHTML = `<h3 style="font-size:13px;font-weight:700;color:#38bdf8;text-transform:uppercase;margin:0 0 10px 0;">Fahrerprofil</h3>`;

    const nameRow = document.createElement('div');
    nameRow.style.cssText = `display:flex;flex-direction:column;gap:6px;`;
    nameRow.innerHTML = `<label style="font-size:12px;color:#94a3b8;font-weight:600;">Fahrschüler Name:</label>`;

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = this.profile.driverName;
    nameInput.style.cssText = `
      background: #1e293b;
      border: 1px solid #475569;
      border-radius: 8px;
      padding: 10px 14px;
      color: white;
      font-size: 14px;
      font-weight: 600;
      outline: none;
    `;
    nameInput.addEventListener('input', () => {
      this.profile.driverName = nameInput.value;
      this.saveProfile();
    });
    nameRow.appendChild(nameInput);
    profileSec.appendChild(nameRow);
    card.appendChild(profileSec);

    // 2b. Audio & Voice Settings (Fahrprüfer Müller & Motor-Sounds)
    const audioSec = document.createElement('div');
    audioSec.style.cssText = `
      background: linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.85) 100%);
      border: 1.5px solid #0284c7;
      border-radius: 14px;
      padding: 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    `;
    audioSec.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="font-size:22px;">🔊</span>
          <div>
            <div style="font-size:14px;font-weight:800;color:#f8fafc;font-family:'Chakra Petch', sans-serif;">SOUND &amp; FAHRPRÜFER-STIMME</div>
            <div style="font-size:11.5px;color:#94a3b8;">Motorsound, Blinker, Signalton &amp; Sprachausgabe (TÜV Prüfer Müller)</div>
          </div>
        </div>
      </div>
    `;

    const audioBtnRow = document.createElement('div');
    audioBtnRow.style.cssText = `display:flex;gap:10px;align-items:center;flex-wrap:wrap;`;

    const btnToggleVoice = document.createElement('button');
    btnToggleVoice.id = 'settings-btn-toggle-voice';
    const updateVoiceBtnVisual = () => {
      const enabled = !!this.profile.examinerVoiceEnabled;
      btnToggleVoice.setAttribute('data-talkback', enabled ? 'on' : 'off');
      btnToggleVoice.innerHTML = enabled
        ? '🔊 Prüfer Talk-Back / TTS: AN (Sprachausgabe &amp; Prüfer-Dialog aktiv)'
        : '🔇 Prüfer Talk-Back / TTS: AUS (Standard — Nur Richtungs- &amp; Fehleranzeige)';
      btnToggleVoice.style.cssText = `
        background: ${enabled ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'rgba(30, 41, 59, 0.9)'};
        color: ${enabled ? '#ffffff' : '#cbd5e1'};
        border: 1.5px solid ${enabled ? '#34d399' : '#64748b'};
        padding: 10px 16px;
        border-radius: 10px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        touch-action: manipulation;
        display: flex;
        align-items: center;
        gap: 6px;
      `;
    };
    updateVoiceBtnVisual();
    btnToggleVoice.addEventListener('click', () => {
      this.profile.examinerVoiceEnabled = !this.profile.examinerVoiceEnabled;
      if (!this.profile.examinerVoiceEnabled) {
        this.audioManager.stopExaminerSpeech();
      }
      this.saveProfile();
      updateVoiceBtnVisual();
      if (this.onToggleTalkBackCallback) {
        this.onToggleTalkBackCallback(this.profile.examinerVoiceEnabled);
      }
    });

    const btnTestSound = document.createElement('button');
    btnTestSound.id = 'settings-btn-test-sound';
    btnTestSound.innerHTML = '🔊 Sound- &amp; Sprachtest abspielen';
    btnTestSound.style.cssText = `
      background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
      color: white;
      border: 1px solid #38bdf8;
      padding: 10px 16px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      box-shadow: 0 4px 14px rgba(2, 132, 199, 0.4);
      display: flex;
      align-items: center;
      gap: 6px;
    `;
    btnTestSound.addEventListener('click', () => {
      btnTestSound.innerText = '⏳ Spiele Sound & Stimme...';
      this.audioManager.unlockAudio();
      this.audioManager.testAudio().then(() => {
        btnTestSound.innerHTML = '✅ Test erfolgreich abgespielt!';
        setTimeout(() => {
          btnTestSound.innerHTML = '🔊 Sound- &amp; Sprachtest abspielen';
        }, 3000);
      });
    });

    const audioTip = document.createElement('div');
    audioTip.style.cssText = `font-size:11px;color:#94a3b8;line-height:1.35;`;
    audioTip.innerHTML = `💡 <i>Ablenkungsfreies Fahren: Talk-Back / TTS ist standardmäßig <b>AUS</b> — es werden nur die kompakten Richtungsanweisungen und Fahrfehler angezeigt. Jederzeit mit <b>[U]</b> oder im HUD umschaltbar.</i>`;

    audioBtnRow.appendChild(btnToggleVoice);
    audioBtnRow.appendChild(btnTestSound);
    audioSec.appendChild(audioBtnRow);
    audioSec.appendChild(audioTip);
    card.appendChild(audioSec);

    // 3a. Cockpit & Mirror Settings
    const cockpitSec = document.createElement('div');
    cockpitSec.style.cssText = `
      background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%);
      border: 1px solid #3b82f6;
      border-radius: 12px;
      padding: 14px 18px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 14px;
    `;
    cockpitSec.innerHTML = `
      <div>
        <div style="font-size:14px;font-weight:800;color:#f8fafc;display:flex;align-items:center;gap:6px;">
          <span>🪑</span> <span>Sitz- & Spiegeleinstellung (Live im Cockpit)</span>
        </div>
        <div style="font-size:12px;color:#94a3b8;margin-top:3px;line-height:1.4;">
          Sitzhöhe, Pedalabstand und alle 3 Spiegel werden <b>direkt live im Cockpit</b> während des Fahrens justiert.
        </div>
      </div>
    `;

    const btnOpenInCockpit = document.createElement('button');
    btnOpenInCockpit.innerText = '🪑 Im Cockpit anpassen';
    btnOpenInCockpit.style.cssText = `
      background: #2563eb;
      color: white;
      border: 1px solid #3b82f6;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(37,99,235,0.4);
    `;
    btnOpenInCockpit.addEventListener('click', () => {
      this.close();
      if (this.onOpenCockpitAdjusterCallback) {
        this.onOpenCockpitAdjusterCallback();
      }
    });
    cockpitSec.appendChild(btnOpenInCockpit);
    card.appendChild(cockpitSec);

    // 3. Android APK Direct Download Banner
    const apkBanner = document.createElement('div');
    apkBanner.style.cssText = `
      background: linear-gradient(135deg, #1e3a8a 0%, #172554 100%);
      border: 1px solid #3b82f6;
      border-radius: 12px;
      padding: 14px 18px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    `;
    apkBanner.innerHTML = `
      <div>
        <div style="font-size:14px;font-weight:700;color:#f8fafc;">📱 Android App (APK Release)</div>
        <div style="font-size:12px;color:#93c5fd;margin-top:2px;">Auf Android Smartphone installieren & offline üben</div>
      </div>
    `;
    const btnDownloadApk = document.createElement('a');
    btnDownloadApk.href = '/app-debug.apk';
    btnDownloadApk.download = 'FahrschulSimulatorStVO.apk';
    btnDownloadApk.innerText = '⬇️ APK Herunterladen';
    btnDownloadApk.style.cssText = `
      background: #2563eb;
      color: white;
      text-decoration: none;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 700;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(37,99,235,0.4);
    `;
    apkBanner.appendChild(btnDownloadApk);
    card.appendChild(apkBanner);

    // 3b. Mobile Touch Controls Configuration Section
    const mobileSec = document.createElement('div');
    mobileSec.style.cssText = `
      background: linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%);
      border: 1.5px solid #a78bfa;
      border-radius: 14px;
      padding: 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    `;
    mobileSec.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="font-size:20px;">📱</span>
        <div>
          <div style="font-size:14px;font-weight:800;color:#f8fafc;font-family:'Chakra Petch', sans-serif;">MOBILE TOUCH-STEUERUNG</div>
          <div style="font-size:11px;color:#94a3b8;">Ergonomie, Lenkungsart &amp; Tastengröße für Smartphones</div>
        </div>
      </div>
    `;

    // Row 1: Steering Mode (Tasten vs Lenkrad vs Gyro)
    const steerModeRow = document.createElement('div');
    steerModeRow.style.cssText = `display:flex;flex-direction:column;gap:6px;`;
    steerModeRow.innerHTML = `<span style="font-size:12px;font-weight:700;color:#c4b5fd;">1. Lenkungsmodus:</span>`;

    const steerBtnGroup = document.createElement('div');
    steerBtnGroup.style.cssText = `display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:6px;`;

    const modes: { id: MobileSteeringMode; label: string; desc: string }[] = [
      { id: 'slider', label: '↔️ Slider', desc: 'Federnd' },
      { id: 'wheel', label: '🚗 Lenkrad', desc: 'Drehrad' },
      { id: 'gyro', label: '📱 Neigung', desc: 'Neigen' },
      { id: 'arrows', label: '◀▶ Tasten', desc: 'Tasten' },
    ];

    const steerButtons: HTMLButtonElement[] = [];
    modes.forEach((m) => {
      const btn = document.createElement('button');
      const isSelected = (this.profile.mobileSteerMode || 'slider') === m.id;
      btn.innerHTML = `<div style="font-weight:800;font-size:12px;">${m.label}</div><div style="font-size:9.5px;color:#94a3b8;margin-top:2px;">${m.desc}</div>`;
      btn.style.cssText = `
        background: ${isSelected ? '#4338ca' : '#1e293b'};
        border: 1.5px solid ${isSelected ? '#818cf8' : '#475569'};
        color: ${isSelected ? '#ffffff' : '#cbd5e1'};
        padding: 8px 6px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.15s;
        text-align: center;
      `;
      btn.addEventListener('click', () => {
        this.profile.mobileSteerMode = m.id;
        this.saveProfile();
        if (this.mobileHUD) {
          this.mobileHUD.setSteeringMode(m.id);
        }
        steerButtons.forEach((b, idx) => {
          const sel = modes[idx].id === m.id;
          b.style.background = sel ? '#4338ca' : '#1e293b';
          b.style.borderColor = sel ? '#818cf8' : '#475569';
          b.style.color = sel ? '#ffffff' : '#cbd5e1';
        });
      });
      steerButtons.push(btn);
      steerBtnGroup.appendChild(btn);
    });
    steerModeRow.appendChild(steerBtnGroup);
    mobileSec.appendChild(steerModeRow);

    // Row 2: Handedness & Button Scale
    const layoutRow = document.createElement('div');
    layoutRow.style.cssText = `display:grid;grid-template-columns:1fr 1fr;gap:10px;`;

    // Handedness
    const handBox = document.createElement('div');
    handBox.style.cssText = `display:flex;flex-direction:column;gap:5px;`;
    handBox.innerHTML = `<span style="font-size:12px;font-weight:700;color:#c4b5fd;">2. Belegung (Händigkeit):</span>`;

    const handBtns = document.createElement('div');
    handBtns.style.cssText = `display:flex;gap:6px;`;
    const isRightHand = (this.profile.mobileHandedness || 'right') === 'right';
    const btnRightHand = document.createElement('button');
    btnRightHand.innerText = '✋ Rechtshänder';
    btnRightHand.style.cssText = `
      flex: 1; padding: 7px 8px; font-size: 11px; font-weight: 700; border-radius: 6px; cursor: pointer;
      background: ${isRightHand ? '#2563eb' : '#1e293b'};
      border: 1px solid ${isRightHand ? '#60a5fa' : '#475569'};
      color: ${isRightHand ? '#ffffff' : '#94a3b8'};
    `;
    const btnLeftHand = document.createElement('button');
    btnLeftHand.innerText = '🤚 Linkshänder';
    btnLeftHand.style.cssText = `
      flex: 1; padding: 7px 8px; font-size: 11px; font-weight: 700; border-radius: 6px; cursor: pointer;
      background: ${!isRightHand ? '#2563eb' : '#1e293b'};
      border: 1px solid ${!isRightHand ? '#60a5fa' : '#475569'};
      color: ${!isRightHand ? '#ffffff' : '#94a3b8'};
    `;
    btnRightHand.addEventListener('click', () => {
      this.profile.mobileHandedness = 'right';
      this.saveProfile();
      if (this.mobileHUD) this.mobileHUD.setHandedness('right');
      btnRightHand.style.background = '#2563eb';
      btnRightHand.style.borderColor = '#60a5fa';
      btnRightHand.style.color = '#ffffff';
      btnLeftHand.style.background = '#1e293b';
      btnLeftHand.style.borderColor = '#475569';
      btnLeftHand.style.color = '#94a3b8';
    });
    btnLeftHand.addEventListener('click', () => {
      this.profile.mobileHandedness = 'left';
      this.saveProfile();
      if (this.mobileHUD) this.mobileHUD.setHandedness('left');
      btnLeftHand.style.background = '#2563eb';
      btnLeftHand.style.borderColor = '#60a5fa';
      btnLeftHand.style.color = '#ffffff';
      btnRightHand.style.background = '#1e293b';
      btnRightHand.style.borderColor = '#475569';
      btnRightHand.style.color = '#94a3b8';
    });
    handBtns.appendChild(btnRightHand);
    handBtns.appendChild(btnLeftHand);
    handBox.appendChild(handBtns);
    layoutRow.appendChild(handBox);

    // Button Size
    const scaleBox = document.createElement('div');
    scaleBox.style.cssText = `display:flex;flex-direction:column;gap:5px;`;
    scaleBox.innerHTML = `<span style="font-size:12px;font-weight:700;color:#c4b5fd;">3. Tastengröße:</span>`;

    const scaleBtns = document.createElement('div');
    scaleBtns.style.cssText = `display:flex;gap:6px;`;
    const isNormalScale = (this.profile.mobileButtonScale || 'normal') === 'normal';
    const btnScaleNormal = document.createElement('button');
    btnScaleNormal.innerText = 'Standard';
    btnScaleNormal.style.cssText = `
      flex: 1; padding: 7px 8px; font-size: 11px; font-weight: 700; border-radius: 6px; cursor: pointer;
      background: ${isNormalScale ? '#2563eb' : '#1e293b'};
      border: 1px solid ${isNormalScale ? '#60a5fa' : '#475569'};
      color: ${isNormalScale ? '#ffffff' : '#94a3b8'};
    `;
    const btnScaleLarge = document.createElement('button');
    btnScaleLarge.innerText = 'Groß (+20%)';
    btnScaleLarge.style.cssText = `
      flex: 1; padding: 7px 8px; font-size: 11px; font-weight: 700; border-radius: 6px; cursor: pointer;
      background: ${!isNormalScale ? '#2563eb' : '#1e293b'};
      border: 1px solid ${!isNormalScale ? '#60a5fa' : '#475569'};
      color: ${!isNormalScale ? '#ffffff' : '#94a3b8'};
    `;
    btnScaleNormal.addEventListener('click', () => {
      this.profile.mobileButtonScale = 'normal';
      this.saveProfile();
      if (this.mobileHUD) this.mobileHUD.setButtonScale('normal');
      btnScaleNormal.style.background = '#2563eb';
      btnScaleNormal.style.borderColor = '#60a5fa';
      btnScaleNormal.style.color = '#ffffff';
      btnScaleLarge.style.background = '#1e293b';
      btnScaleLarge.style.borderColor = '#475569';
      btnScaleLarge.style.color = '#94a3b8';
    });
    btnScaleLarge.addEventListener('click', () => {
      this.profile.mobileButtonScale = 'large';
      this.saveProfile();
      if (this.mobileHUD) this.mobileHUD.setButtonScale('large');
      btnScaleLarge.style.background = '#2563eb';
      btnScaleLarge.style.borderColor = '#60a5fa';
      btnScaleLarge.style.color = '#ffffff';
      btnScaleNormal.style.background = '#1e293b';
      btnScaleNormal.style.borderColor = '#475569';
      btnScaleNormal.style.color = '#94a3b8';
    });
    scaleBtns.appendChild(btnScaleNormal);
    scaleBtns.appendChild(btnScaleLarge);
    scaleBox.appendChild(scaleBtns);
    layoutRow.appendChild(scaleBox);

    mobileSec.appendChild(layoutRow);
    card.appendChild(mobileSec);

    // 4. Steering Wheel & Pedals Section (Sim-Rig Kalibrierung)
    this.buildWheelSection(card);

    // 5. Keyboard Mappings Section
    const keySec = document.createElement('div');
    keySec.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <h3 style="font-size:13px;font-weight:700;color:#38bdf8;text-transform:uppercase;margin:0;">Tastaturbelegung (Klicken zum Ändern)</h3>
      </div>
    `;

    const keyGrid = document.createElement('div');
    keyGrid.style.cssText = `display:grid;grid-template-columns:1fr 1fr;gap:8px;max-height:220px;overflow-y:auto;padding-right:4px;`;

    const actions = [
      { key: 'throttle', label: '⚡ Gas (Beschleunigen)' },
      { key: 'brake', label: '🛑 Bremse' },
      { key: 'steerLeft', label: '⬅️ Lenken Links' },
      { key: 'steerRight', label: '➡️ Lenken Rechts' },
      { key: 'seatbelt', label: '🔒 Sicherheitsgurt (B)' },
      { key: 'handbrake', label: '🅿️ Handbremse (Toggle)' },
      { key: 'gearUp', label: '⬆️ Gang hoch / R (Automatik: →D)' },
      { key: 'gearDown', label: '⬇️ Gang runter / G (Automatik: →P)' },
      { key: 'clutch', label: '🕹️ Kupplung (Halten, nur Manuell)' },
      { key: 'blinkerLeft', label: '◀️ Blinker Links' },
      { key: 'blinkerRight', label: '▶️ Blinker Rechts' },
      { key: 'hazard', label: '⚠️ Warnblinker' },
      { key: 'shoulderLeft', label: '👀 Schulterblick Links' },
      { key: 'shoulderRight', label: '👀 Schulterblick Rechts' },
      { key: 'lookInterior', label: '🔍 Innenspiegel' },
      { key: 'lookExterior', label: '🔍 Außenspiegel' },
      { key: 'cameraToggle', label: '🎥 Kamera wechseln' },
      { key: 'horn', label: '📢 Hupe' },
      { key: 'engineRestart', label: '🔑 Motor An/Aus (I = Zündung)' },
    ];

    this.keybindButtons.clear();

    actions.forEach((act) => {
      const row = document.createElement('div');
      row.style.cssText = `
        background: #1e293b;
        border: 1px solid #334155;
        border-radius: 8px;
        padding: 8px 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      `;

      const label = document.createElement('span');
      label.style.cssText = `font-size:12.5px;color:#e2e8f0;font-weight:600;`;
      label.innerText = act.label;

      const btn = document.createElement('button');
      const currentVal = this.getKeyDisplay(act.key);
      btn.innerText = currentVal;
      btn.style.cssText = `
        background: #0f172a;
        color: #38bdf8;
        border: 1px solid #0284c7;
        border-radius: 6px;
        padding: 4px 10px;
        font-family: 'Chakra Petch', monospace;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        min-width: 60px;
      `;

      btn.addEventListener('click', () => {
        this.startListeningForKey(act.key, btn);
      });

      this.keybindButtons.set(act.key, btn);

      row.appendChild(label);
      row.appendChild(btn);
      keyGrid.appendChild(row);
    });

    keySec.appendChild(keyGrid);
    card.appendChild(keySec);

    // 5. Footer Buttons
    const footer = document.createElement('div');
    footer.style.cssText = `display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:4px;`;

    const btnResetKeys = document.createElement('button');
    btnResetKeys.innerText = '↺ Standard wiederherstellen';
    btnResetKeys.style.cssText = `
      background: #334155;
      color: #94a3b8;
      border: none;
      padding: 10px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
    `;
    btnResetKeys.addEventListener('click', () => {
      this.desktopInput.resetKeyMap();
      this.buildUI();
    });

    const btnSave = document.createElement('button');
    btnSave.innerText = '💾 Speichern & Schließen';
    btnSave.style.cssText = `
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      color: white;
      border: none;
      padding: 10px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
    `;
    btnSave.addEventListener('click', () => this.close());

    footer.appendChild(btnResetKeys);
    footer.appendChild(btnSave);
    card.appendChild(footer);

    this.container.appendChild(card);
    this.startWheelLiveMonitor();
  }

  private wizardQueue: Array<{ key: string; label: string }> = [];

  private buildWheelSection(card: HTMLElement) {
    const wheelSec = document.createElement('div');
    wheelSec.id = 'settings-wheel-section';
    wheelSec.style.cssText = `
      background: linear-gradient(135deg, rgba(220, 38, 38, 0.10) 0%, rgba(16, 185, 129, 0.08) 50%, rgba(15, 23, 42, 0.94) 100%);
      border: 1.5px solid #10b981;
      border-radius: 14px;
      padding: 14px 18px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    `;

    const cfg = this.desktopInput.wheelConfig;

    // Header with live device name badge
    const headerRow = document.createElement('div');
    headerRow.style.cssText = `display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px;`;
    headerRow.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="font-size:22px;">🏎️</span>
        <div>
          <div style="font-size:14px;font-weight:800;color:#f8fafc;font-family:'Chakra Petch', sans-serif;">HORI MARIO KART RACING WHEEL PRO DX &amp; PEDALE</div>
          <div style="font-size:11.5px;color:#94a3b8;">NSX-123E / NSW-228U (28 cm Lenkrad + Abnehmbare Gas- &amp; Bremspedale, 270°/180°, 7-Stufen)</div>
        </div>
      </div>
    `;

    const deviceBadge = document.createElement('div');
    deviceBadge.id = 'wheel-device-badge';
    deviceBadge.style.cssText = `
      font-size: 11.5px;
      font-weight: 700;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(16, 185, 129, 0.2);
      border: 1px solid #10b981;
      color: #34d399;
      font-family: monospace;
    `;
    deviceBadge.innerText = cfg.deviceName ? `🟢 ${cfg.deviceName}` : '⚪ Lenkrad bereit (Taste/Pedal drücken)';
    headerRow.appendChild(deviceBadge);
    wheelSec.appendChild(headerRow);

    // 1. Hardware Preset & 3-Step Guided Wizard Row
    const presetRow = document.createElement('div');
    presetRow.style.cssText = `display:flex;gap:6px;flex-wrap:wrap;align-items:center;justify-content:space-between;background:#0f172a;padding:8px 10px;border-radius:10px;border:1px solid #334155;`;

    const presetBtns = document.createElement('div');
    presetBtns.style.cssText = `display:flex;gap:6px;flex-wrap:wrap;`;
    const presets: Array<{ id: 'hori_pro_dx' | 'logitech_g29' | 'thrustmaster'; label: string }> = [
      { id: 'hori_pro_dx', label: '🔴 HORI Pro DX (NSX-123E)' },
      { id: 'logitech_g29', label: '🔵 Logitech G29/G920' },
      { id: 'thrustmaster', label: '🟡 Thrustmaster T150/T248' },
    ];
    presets.forEach((p) => {
      const btn = document.createElement('button');
      const active = (cfg.presetId || 'hori_pro_dx') === p.id;
      btn.id = `wheel-preset-${p.id}`;
      btn.innerText = p.label;
      btn.style.cssText = `
        background: ${active ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' : '#1e293b'};
        color: ${active ? '#ffffff' : '#cbd5e1'};
        border: 1px solid ${active ? '#f87171' : '#475569'};
        border-radius: 6px;
        padding: 5px 10px;
        font-size: 11px;
        font-weight: 800;
        cursor: pointer;
      `;
      btn.addEventListener('click', () => {
        this.desktopInput.applyWheelPreset(p.id);
        this.buildUI();
      });
      presetBtns.appendChild(btn);
    });
    presetRow.appendChild(presetBtns);

    const btnWizard = document.createElement('button');
    btnWizard.id = 'wheel-btn-3step-wizard';
    btnWizard.innerText = '🎯 3-Schritt Pedal-Assistent';
    btnWizard.title = 'Kalibriert nacheinander: 1. Lenkrad -> 2. Rechtes Gaspedal -> 3. Linkes Bremspedal';
    btnWizard.style.cssText = `
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      color: #ffffff;
      border: 1px solid #34d399;
      border-radius: 6px;
      padding: 5px 10px;
      font-size: 11px;
      font-weight: 800;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.35);
    `;
    btnWizard.addEventListener('click', () => {
      this.wizardQueue = [
        { key: 'throttle', label: '⚡ Gaspedal (Rechtes Pedal)' },
        { key: 'brake', label: '🛑 Bremspedal (Linkes Pedal)' },
      ];
      this.startCalibratingWheel('steer', '🔄 Schritt 1/3: Lenkrad');
    });
    presetRow.appendChild(btnWizard);
    wheelSec.appendChild(presetRow);

    // 2. HORI Pro DX Hardware Switches (270°/180° Rotation, Paddle Mode, Detachable Pedal Mode)
    const hwModeRow = document.createElement('div');
    hwModeRow.style.cssText = `display:grid;grid-template-columns:repeat(auto-fit, minmax(185px, 1fr));gap:8px;`;

    // 2a. Rotation Angle (270° vs 180° vs 900°)
    const rotBox = document.createElement('div');
    rotBox.style.cssText = `background:#1e293b;border:1px solid #334155;border-radius:8px;padding:7px 10px;display:flex;flex-direction:column;gap:5px;`;
    rotBox.innerHTML = `<span style="font-size:11px;font-weight:700;color:#fca5a5;">🔄 HORI Lenkwinkel-Schalter:</span>`;
    const rotBtns = document.createElement('div');
    rotBtns.style.cssText = `display:flex;gap:4px;`;
    ([270, 180, 900] as const).forEach((deg) => {
      const b = document.createElement('button');
      const sel = (cfg.rotationDegrees || 270) === deg;
      b.innerText = deg === 270 ? '270° (Pro DX)' : deg === 180 ? '180° (Schnell)' : '900°';
      b.style.cssText = `
        flex:1;padding:4px 6px;font-size:10.5px;font-weight:700;border-radius:5px;cursor:pointer;
        background:${sel ? '#2563eb' : '#0f172a'};
        border:1px solid ${sel ? '#60a5fa' : '#475569'};
        color:${sel ? '#ffffff' : '#94a3b8'};
      `;
      b.addEventListener('click', () => {
        this.desktopInput.setRotationDegrees(deg);
        this.buildUI();
      });
      rotBtns.appendChild(b);
    });
    rotBox.appendChild(rotBtns);
    hwModeRow.appendChild(rotBox);

    // 2b. Shift Paddles Function (Blinkers vs Gears)
    const padModeBox = document.createElement('div');
    padModeBox.style.cssText = `background:#1e293b;border:1px solid #334155;border-radius:8px;padding:7px 10px;display:flex;flex-direction:column;gap:5px;`;
    padModeBox.innerHTML = `<span style="font-size:11px;font-weight:700;color:#34d399;">🕹️ L/R Schaltwippen am Lenkrad:</span>`;
    const padModeBtns = document.createElement('div');
    padModeBtns.style.cssText = `display:flex;gap:4px;`;
    ([
      { id: 'blinkers' as const, label: '◀▶ Blinker L/R' },
      { id: 'gears' as const, label: '⚙️ Gang -/+' },
    ]).forEach((pm) => {
      const b = document.createElement('button');
      const sel = (cfg.paddleMode || 'blinkers') === pm.id;
      b.innerText = pm.label;
      b.style.cssText = `
        flex:1;padding:4px 6px;font-size:10.5px;font-weight:700;border-radius:5px;cursor:pointer;
        background:${sel ? '#059669' : '#0f172a'};
        border:1px solid ${sel ? '#34d399' : '#475569'};
        color:${sel ? '#ffffff' : '#94a3b8'};
      `;
      b.addEventListener('click', () => {
        this.desktopInput.setPaddleMode(pm.id);
        this.buildUI();
      });
      padModeBtns.appendChild(b);
    });
    padModeBox.appendChild(padModeBtns);
    hwModeRow.appendChild(padModeBox);

    // 2c. Detachable Gas & Brake Pedal Signal Mode
    const pedalModeBox = document.createElement('div');
    pedalModeBox.style.cssText = `background:#1e293b;border:1px solid #334155;border-radius:8px;padding:7px 10px;display:flex;flex-direction:column;gap:5px;`;
    pedalModeBox.innerHTML = `<span style="font-size:11px;font-weight:700;color:#38bdf8;">🦶 Abnehmbare Gas-/Bremspedale:</span>`;
    const pedalSelect = document.createElement('select');
    pedalSelect.id = 'wheel-pedal-mode-select';
    pedalSelect.style.cssText = `background:#0f172a;color:#f8fafc;border:1px solid #475569;border-radius:5px;padding:4px 6px;font-size:10.5px;font-weight:700;cursor:pointer;`;
    ([
      { id: 'auto', label: '✨ Auto (ZR/ZL + Kombi-Achse)' },
      { id: 'triggers', label: '🎮 ZR / ZL Trigger (Taste 7 & 6)' },
      { id: 'separate_axes', label: '📊 Getrennte Achsen (2 Kanäle)' },
      { id: 'combined_axis', label: '🔗 Kombinierte Achse (1 Kabel Split)' },
    ]).forEach((opt) => {
      const o = document.createElement('option');
      o.value = opt.id;
      o.textContent = opt.label;
      if ((cfg.pedalMode || 'auto') === opt.id) o.selected = true;
      pedalSelect.appendChild(o);
    });
    pedalSelect.addEventListener('change', () => {
      cfg.pedalMode = pedalSelect.value as any;
      this.desktopInput.saveWheelConfig(cfg);
      this.buildUI();
    });
    pedalModeBox.appendChild(pedalSelect);
    hwModeRow.appendChild(pedalModeBox);

    wheelSec.appendChild(hwModeRow);

    // Calibration banner
    const calibBanner = document.createElement('div');
    calibBanner.id = 'wheel-calib-banner';
    calibBanner.style.cssText = `
      display: none;
      background: #eab308;
      color: #0f172a;
      font-weight: 700;
      font-size: 12.5px;
      padding: 8px 12px;
      border-radius: 8px;
      text-align: center;
    `;
    this.calibrationStatusElem = calibBanner;
    wheelSec.appendChild(calibBanner);

    // Axis Calibration Grid (Steering, Gas, Brake, Clutch)
    const axisGrid = document.createElement('div');
    axisGrid.style.cssText = `display:grid;grid-template-columns:1fr 1fr;gap:10px;`;

    // Helper to create an axis / pedal row
    const createAxisRow = (
      label: string,
      targetKey: 'steer' | 'throttle' | 'brake' | 'clutch',
      meterId: string,
      getAxisText: () => string,
      getInverted: () => boolean,
      setInverted: (inv: boolean) => void
    ) => {
      const box = document.createElement('div');
      box.style.cssText = `
        background: #1e293b;
        border: 1px solid #334155;
        border-radius: 8px;
        padding: 8px 12px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      `;

      const top = document.createElement('div');
      top.style.cssText = `display:flex;justify-content:space-between;align-items:center;`;
      top.innerHTML = `<span style="font-size:12.5px;color:#e2e8f0;font-weight:700;">${label}</span>`;

      const btnCalib = document.createElement('button');
      btnCalib.innerText = '⚙️ Kalibrieren';
      btnCalib.style.cssText = `
        background: #0f172a;
        color: #34d399;
        border: 1px solid #10b981;
        border-radius: 6px;
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
      `;
      btnCalib.addEventListener('click', () => {
        this.wizardQueue = [];
        this.startCalibratingWheel(targetKey, label);
      });
      top.appendChild(btnCalib);
      box.appendChild(top);

      // Meter bar
      const meterTrack = document.createElement('div');
      meterTrack.style.cssText = `
        width: 100%;
        height: 12px;
        background: #0f172a;
        border-radius: 6px;
        overflow: hidden;
        position: relative;
        border: 1px solid #334155;
      `;
      const meterFill = document.createElement('div');
      meterFill.id = meterId;
      const barColor = targetKey === 'brake'
        ? 'linear-gradient(90deg, #dc2626, #f87171)'
        : targetKey === 'steer'
        ? 'linear-gradient(90deg, #2563eb, #38bdf8)'
        : 'linear-gradient(90deg, #10b981, #34d399)';
      meterFill.style.cssText = `
        height: 100%;
        width: ${targetKey === 'steer' ? '50%' : '0%'};
        background: ${barColor};
        border-radius: 6px;
        transition: width 0.05s ease-out;
      `;
      meterTrack.appendChild(meterFill);
      box.appendChild(meterTrack);

      // Controls row: Mapping label & Invert checkbox
      const btm = document.createElement('div');
      btm.style.cssText = `display:flex;justify-content:space-between;align-items:center;font-size:11px;color:#94a3b8;`;
      const mapLabel = document.createElement('span');
      mapLabel.id = `${meterId}-label`;
      mapLabel.innerText = getAxisText();
      btm.appendChild(mapLabel);

      const invLabel = document.createElement('label');
      invLabel.style.cssText = `display:flex;align-items:center;gap:4px;cursor:pointer;`;
      const invCheck = document.createElement('input');
      invCheck.type = 'checkbox';
      invCheck.checked = getInverted();
      invCheck.addEventListener('change', () => {
        setInverted(invCheck.checked);
        this.desktopInput.saveWheelConfig(this.desktopInput.wheelConfig);
      });
      invLabel.appendChild(invCheck);
      const invSpan = document.createElement('span');
      invSpan.innerText = 'Invertieren';
      invLabel.appendChild(invSpan);
      btm.appendChild(invLabel);

      box.appendChild(btm);
      return box;
    };

    // 1. Steering
    axisGrid.appendChild(
      createAxisRow(
        `🔄 Lenkrad (${cfg.rotationDegrees || 270}°)`,
        'steer',
        'wheel-steer-meter',
        () => `Achse ${cfg.steerAxis}`,
        () => cfg.steerInvert,
        (inv) => { cfg.steerInvert = inv; }
      )
    );

    // 2. Throttle (Right Detachable Pedal)
    axisGrid.appendChild(
      createAxisRow(
        '⚡ Gaspedal (Rechtes Pedal)',
        'throttle',
        'wheel-throttle-meter',
        () => cfg.pedalMode === 'combined_axis'
          ? 'Kombi-Achse 1 (-)'
          : (cfg.throttle.type === 'axis' ? `Achse ${cfg.throttle.index}` : `ZR / Taste ${cfg.throttle.index}`),
        () => !!cfg.throttle.invert,
        (inv) => { cfg.throttle.invert = inv; }
      )
    );

    // 3. Brake (Left Detachable Pedal)
    axisGrid.appendChild(
      createAxisRow(
        '🛑 Bremspedal (Linkes Pedal)',
        'brake',
        'wheel-brake-meter',
        () => cfg.pedalMode === 'combined_axis'
          ? 'Kombi-Achse 1 (+)'
          : (cfg.brake.type === 'axis' ? `Achse ${cfg.brake.index}` : `ZL / Taste ${cfg.brake.index}`),
        () => !!cfg.brake.invert,
        (inv) => { cfg.brake.invert = inv; }
      )
    );

    // 4. Clutch (Optional)
    axisGrid.appendChild(
      createAxisRow(
        '🕹️ Kupplung (Optional)',
        'clutch',
        'wheel-clutch-meter',
        () => cfg.clutch ? (cfg.clutch.type === 'axis' ? `Achse ${cfg.clutch.index}` : `Taste ${cfg.clutch.index}`) : 'Automatik (Nicht belegt)',
        () => !!(cfg.clutch?.invert),
        (inv) => {
          if (!cfg.clutch) cfg.clutch = { type: 'axis', index: 1, invert: inv };
          else cfg.clutch.invert = inv;
        }
      )
    );

    wheelSec.appendChild(axisGrid);

    // Deadzone & HORI 7-Level Sensitivity Sliders
    const sliderRow = document.createElement('div');
    sliderRow.style.cssText = `display:flex;gap:12px;align-items:center;background:#1e293b;padding:8px 12px;border-radius:8px;border:1px solid #334155;flex-wrap:wrap;`;

    const currSens = cfg.sensitivityLevel || 4;
    sliderRow.innerHTML = `
      <div style="flex:1;min-width:180px;display:flex;flex-direction:column;gap:4px;">
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#e2e8f0;font-weight:600;">
          <span>Totbereich (Deadzone)</span>
          <span id="wheel-deadzone-val">${Math.round(cfg.steerDeadzone * 100)}%</span>
        </div>
        <input id="wheel-deadzone-slider" type="range" min="0" max="15" step="0.5" value="${cfg.steerDeadzone * 100}" style="width:100%;accent-color:#10b981;cursor:pointer;">
      </div>
      <div style="flex:1;min-width:180px;display:flex;flex-direction:column;gap:4px;">
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#e2e8f0;font-weight:600;">
          <span>HORI 7-Stufen Empfindlichkeit</span>
          <span id="wheel-sens-val">Stufe ${currSens} / 7 (Lin ${cfg.steerLinearity.toFixed(1)})</span>
        </div>
        <input id="wheel-sens-slider" type="range" min="1" max="7" step="1" value="${currSens}" style="width:100%;accent-color:#ef4444;cursor:pointer;">
      </div>
    `;
    wheelSec.appendChild(sliderRow);

    const dzSlider = sliderRow.querySelector('#wheel-deadzone-slider') as HTMLInputElement;
    if (dzSlider) {
      dzSlider.addEventListener('input', () => {
        cfg.steerDeadzone = parseFloat(dzSlider.value) / 100;
        const v = sliderRow.querySelector('#wheel-deadzone-val');
        if (v) v.textContent = `${dzSlider.value}%`;
        this.desktopInput.saveWheelConfig(cfg);
      });
    }

    const sensSlider = sliderRow.querySelector('#wheel-sens-slider') as HTMLInputElement;
    if (sensSlider) {
      sensSlider.addEventListener('input', () => {
        const lvl = parseInt(sensSlider.value, 10);
        this.desktopInput.setSensitivityLevel(lvl);
        const v = sliderRow.querySelector('#wheel-sens-val');
        if (v) v.textContent = `Stufe ${lvl} / 7 (Lin ${this.desktopInput.wheelConfig.steerLinearity.toFixed(1)})`;
      });
    }

    // Paddles, StVO Mirrors, Shoulder Checks & Wheel Buttons Grid
    const btnSection = document.createElement('div');
    btnSection.style.cssText = `display:flex;flex-direction:column;gap:6px;`;
    btnSection.innerHTML = `<span style="font-size:12px;font-weight:700;color:#34d399;text-transform:uppercase;">HORI Lenkrad-Tasten, Schaltwippen &amp; StVO Spiegel-/Schulterblick</span>`;

    const btnGrid = document.createElement('div');
    btnGrid.style.cssText = `display:grid;grid-template-columns:repeat(auto-fill, minmax(150px, 1fr));gap:6px;`;

    const wheelBtns = [
      { key: 'buttonBlinkerLeft', label: '◀️ Blinker L (L-Paddel)' },
      { key: 'buttonBlinkerRight', label: '▶️ Blinker R (R-Paddel)' },
      { key: 'buttonShoulderLeft', label: '👀 Schulter L (Taste Y)' },
      { key: 'buttonShoulderRight', label: '👀 Schulter R (Taste A)' },
      { key: 'buttonLookInterior', label: '🪞 Innenspiegel (D-Hoch)' },
      { key: 'buttonLookExteriorLeft', label: '🪞 Spiegel L (D-Links)' },
      { key: 'buttonLookExteriorRight', label: '🪞 Spiegel R (D-Rechts)' },
      { key: 'buttonHazard', label: '⚠️ Warnblinker (D-Runter)' },
      { key: 'buttonGearUp', label: '⬆️ Gang D / + (Taste X)' },
      { key: 'buttonGearDown', label: '⬇️ Gang R / P (Taste B)' },
      { key: 'buttonHandbrake', label: '🅿️ Handbremse (LSB)' },
      { key: 'buttonSeatbelt', label: '🔒 Gurt (RSB)' },
      { key: 'buttonEngine', label: '🔑 Motor Start (+)' },
      { key: 'buttonCamera', label: '🎥 Kamera (-)' },
      { key: 'buttonHorn', label: '📢 Hupe (HOME)' },
    ];

    wheelBtns.forEach((wb) => {
      const bRow = document.createElement('div');
      bRow.style.cssText = `
        background: #1e293b;
        border: 1px solid #334155;
        border-radius: 6px;
        padding: 6px 8px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
      `;
      const bLabel = document.createElement('span');
      bLabel.style.cssText = `font-size:10.5px;color:#cbd5e1;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
      bLabel.innerText = wb.label;

      const bBtn = document.createElement('button');
      const currVal = (cfg as any)[wb.key];
      bBtn.innerText = currVal !== undefined ? `Taste ${currVal}` : '—';
      bBtn.style.cssText = `
        background: #0f172a;
        color: #34d399;
        border: 1px solid #059669;
        border-radius: 4px;
        padding: 3px 6px;
        font-family: monospace;
        font-size: 10.5px;
        font-weight: 700;
        cursor: pointer;
        min-width: 50px;
      `;
      bBtn.addEventListener('click', () => {
        this.wizardQueue = [];
        this.startCalibratingWheel(wb.key, wb.label);
      });
      bRow.appendChild(bLabel);
      bRow.appendChild(bBtn);
      btnGrid.appendChild(bRow);
    });

    btnSection.appendChild(btnGrid);
    wheelSec.appendChild(btnSection);

    card.appendChild(wheelSec);
  }

  private startCalibratingWheel(targetKey: string, label: string) {
    this.calibratingTarget = targetKey;
    const gamepads = (typeof navigator !== 'undefined' && navigator.getGamepads) ? navigator.getGamepads() : [];
    const pad = gamepads[this.desktopInput.wheelConfig.gamepadIndex] || gamepads[0];
    if (!pad) {
      if (this.calibrationStatusElem) {
        this.calibrationStatusElem.style.display = 'block';
        this.calibrationStatusElem.style.background = '#ef4444';
        this.calibrationStatusElem.style.color = '#ffffff';
        this.calibrationStatusElem.innerText = '⚠️ Kein Lenkrad erkannt. Bitte HORI USB-Stecker verbinden und eine Taste am Lenkrad drücken.';
        setTimeout(() => {
          if (this.calibrationStatusElem) this.calibrationStatusElem.style.display = 'none';
        }, 3500);
      }
      this.calibratingTarget = null;
      this.wizardQueue = [];
      return;
    }

    // Capture baseline
    this.baselineAxes = pad.axes ? [...pad.axes] : [];
    this.baselineButtons = pad.buttons ? pad.buttons.map(b => b ? b.value : 0) : [];

    if (this.calibrationStatusElem) {
      this.calibrationStatusElem.style.display = 'block';
      this.calibrationStatusElem.style.background = '#eab308';
      this.calibrationStatusElem.style.color = '#0f172a';
      if (targetKey === 'steer') {
        this.calibrationStatusElem.innerText = `🔄 Kalibriere ${label}: Bitte HORI Lenkrad jetzt deutlich nach links oder rechts einschlagen...`;
      } else if (targetKey === 'throttle') {
        this.calibrationStatusElem.innerText = `⚡ Kalibriere ${label}: Bitte RECHTES Gaspedal jetzt VOLL durchtreten...`;
      } else if (targetKey === 'brake') {
        this.calibrationStatusElem.innerText = `🛑 Kalibriere ${label}: Bitte LINKES Bremspedal jetzt VOLL durchtreten...`;
      } else if (targetKey === 'clutch') {
        this.calibrationStatusElem.innerText = `🕹️ Kalibriere ${label}: Bitte Kupplungspedal jetzt VOLL durchtreten...`;
      } else {
        this.calibrationStatusElem.innerText = `🔘 Bitte Taste/Schaltwippe für "${label}" jetzt am HORI Lenkrad drücken...`;
      }
    }
  }

  private advanceWizardOrFinish(successMessage: string) {
    if (this.wizardQueue.length > 0) {
      const next = this.wizardQueue.shift()!;
      if (this.calibrationStatusElem) {
        this.calibrationStatusElem.style.background = '#10b981';
        this.calibrationStatusElem.style.color = '#ffffff';
        this.calibrationStatusElem.innerText = `${successMessage} → Weiter mit ${next.label}...`;
      }
      setTimeout(() => {
        this.startCalibratingWheel(next.key, next.label);
      }, 900);
    } else {
      if (this.calibrationStatusElem) {
        this.calibrationStatusElem.style.background = '#10b981';
        this.calibrationStatusElem.style.color = '#ffffff';
        this.calibrationStatusElem.innerText = `${successMessage} Alle Pedale & Achsen sind einsatzbereit!`;
        setTimeout(() => {
          if (this.calibrationStatusElem) this.calibrationStatusElem.style.display = 'none';
          this.buildUI();
        }, 1200);
      }
    }
  }

  private startWheelLiveMonitor() {
    if (this.pollRafId) return;

    const raf = typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame : (cb: Function) => setTimeout(cb, 50) as any;

    const poll = () => {
      const gamepads = (typeof navigator !== 'undefined' && navigator.getGamepads) ? navigator.getGamepads() : [];
      const cfg = this.desktopInput.wheelConfig;
      const pad = gamepads[cfg.gamepadIndex] || gamepads[0];

      const badge = this.container.querySelector('#wheel-device-badge') as HTMLElement;
      if (badge) {
        badge.innerText = pad ? `🟢 ${pad.id.slice(0, 36)}` : `⚪ ${cfg.deviceName || 'HORI Pro DX'} (Taste drücken)`;
      }

      if (pad) {
        // 1. Steering (50% = center 0°, 0% = full left, 100% = full right)
        const steerFill = this.container.querySelector('#wheel-steer-meter') as HTMLElement;
        if (steerFill && pad.axes[cfg.steerAxis] !== undefined) {
          const raw = pad.axes[cfg.steerAxis];
          const pct = Math.min(100, Math.max(0, ((raw * (cfg.steerInvert ? -1 : 1) + 1) / 2) * 100));
          steerFill.style.width = `${pct}%`;
        }

        // 2. Throttle (Right Detachable Pedal via unified readPedalValue)
        const thFill = this.container.querySelector('#wheel-throttle-meter') as HTMLElement;
        if (thFill) {
          const val = this.desktopInput.readPedalValue(pad, 'throttle');
          thFill.style.width = `${Math.min(100, Math.max(0, val * 100))}%`;
        }

        // 3. Brake (Left Detachable Pedal via unified readPedalValue)
        const brFill = this.container.querySelector('#wheel-brake-meter') as HTMLElement;
        if (brFill) {
          const val = this.desktopInput.readPedalValue(pad, 'brake');
          brFill.style.width = `${Math.min(100, Math.max(0, val * 100))}%`;
        }

        // 4. Clutch
        const clFill = this.container.querySelector('#wheel-clutch-meter') as HTMLElement;
        if (clFill && cfg.clutch) {
          const val = this.desktopInput.readPedalValue(pad, 'clutch');
          clFill.style.width = `${Math.min(100, Math.max(0, val * 100))}%`;
        }

        // Calibration detector
        if (this.calibratingTarget) {
          const target = this.calibratingTarget;
          if (target.startsWith('button')) {
            for (let i = 0; i < pad.buttons.length; i++) {
              if (pad.buttons[i]?.pressed) {
                (cfg as any)[target] = i;
                this.desktopInput.saveWheelConfig(cfg);
                this.calibratingTarget = null;
                this.advanceWizardOrFinish(`✅ Taste ${i} erfolgreich zugewiesen!`);
                break;
              }
            }
          } else if (target === 'steer') {
            for (let a = 0; a < pad.axes.length; a++) {
              const delta = Math.abs(pad.axes[a] - (this.baselineAxes[a] ?? 0));
              if (delta > 0.35) {
                cfg.steerAxis = a;
                this.desktopInput.saveWheelConfig(cfg);
                this.calibratingTarget = null;
                this.advanceWizardOrFinish(`✅ Lenkung auf Achse ${a} kalibriert!`);
                break;
              }
            }
          } else if (target === 'throttle' || target === 'brake' || target === 'clutch') {
            for (let b = 0; b < pad.buttons.length; b++) {
              const deltaB = Math.abs((pad.buttons[b]?.value || 0) - (this.baselineButtons[b] ?? 0));
              if (deltaB > 0.4) {
                const item: WheelMappingItem = { type: 'button', index: b, invert: false };
                (cfg as any)[target] = item;
                this.desktopInput.saveWheelConfig(cfg);
                this.calibratingTarget = null;
                this.advanceWizardOrFinish(
                  `✅ ${target === 'throttle' ? 'Gaspedal' : target === 'brake' ? 'Bremspedal' : 'Kupplung'} auf Trigger/Taste ${b} kalibriert!`
                );
                break;
              }
            }
            if (this.calibratingTarget) {
              for (let a = 0; a < pad.axes.length; a++) {
                const deltaA = Math.abs(pad.axes[a] - (this.baselineAxes[a] ?? 0));
                if (deltaA > 0.35) {
                  const restingVal = this.baselineAxes[a] ?? 0;
                  const pressedVal = pad.axes[a];
                  const item: WheelMappingItem = {
                    type: 'axis',
                    index: a,
                    minVal: restingVal,
                    maxVal: pressedVal,
                    invert: pressedVal < restingVal,
                  };
                  (cfg as any)[target] = item;
                  this.desktopInput.saveWheelConfig(cfg);
                  this.calibratingTarget = null;
                  this.advanceWizardOrFinish(
                    `✅ ${target === 'throttle' ? 'Gaspedal' : target === 'brake' ? 'Bremspedal' : 'Kupplung'} auf Achse ${a} kalibriert!`
                  );
                  break;
                }
              }
            }
          }
        }
      }

      this.pollRafId = raf(poll);
    };

    this.pollRafId = raf(poll);
  }

  private getKeyDisplay(actionKey: string): string {
    const val = (this.desktopInput.keyMap as any)[actionKey];
    if (Array.isArray(val)) {
      return val.map((k) => k.replace('Key', '').replace('Arrow', '↑↓←→ ')).join(' / ');
    }
    return typeof val === 'string' ? val.replace('Key', '').replace('Digit', 'Taste ') : '';
  }

  private startListeningForKey(actionKey: string, btn: HTMLButtonElement) {
    this.listeningActionKey = actionKey;
    btn.innerText = 'Taste drücken...';
    btn.style.background = '#eab308';
    btn.style.color = '#000';
  }

  private setupKeyCapture() {
    window.addEventListener('keydown', (e) => {
      if (!this.listeningActionKey) return;
      e.preventDefault();
      e.stopPropagation();

      const action = this.listeningActionKey;
      const newKey = e.code;

      const current = (this.desktopInput.keyMap as any)[action];
      if (Array.isArray(current)) {
        (this.desktopInput.keyMap as any)[action] = [newKey];
      } else {
        (this.desktopInput.keyMap as any)[action] = newKey;
      }

      this.desktopInput.saveKeyMap(this.desktopInput.keyMap);
      this.listeningActionKey = null;

      const btn = this.keybindButtons.get(action);
      if (btn) {
        btn.innerText = this.getKeyDisplay(action);
        btn.style.background = '#0f172a';
        btn.style.color = '#38bdf8';
      }
    });
  }

  public open() {
    this.buildUI();
  }

  public close() {
    this.unmount();
    if (this.onCloseCallback) this.onCloseCallback();
  }

  public onClose(cb: () => void) {
    this.onCloseCallback = cb;
  }

  public onOpenCockpitAdjuster(cb: () => void) {
    this.onOpenCockpitAdjusterCallback = cb;
  }

  public onToggleTalkBack(cb: (enabled: boolean) => void) {
    this.onToggleTalkBackCallback = cb;
  }

  public setTalkBackEnabled(enabled: boolean) {
    this.profile.examinerVoiceEnabled = enabled;
    this.saveProfile();
    const btn = this.container.querySelector('#settings-btn-toggle-voice') as HTMLElement | null;
    if (btn) {
      btn.setAttribute('data-talkback', enabled ? 'on' : 'off');
      btn.innerHTML = enabled
        ? '🔊 Prüfer Talk-Back / TTS: AN (Sprachausgabe &amp; Prüfer-Dialog aktiv)'
        : '🔇 Prüfer Talk-Back / TTS: AUS (Standard — Nur Richtungs- &amp; Fehleranzeige)';
    }
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
  }

  public unmount() {
    if (this.pollRafId) {
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(this.pollRafId);
      } else {
        clearTimeout(this.pollRafId);
      }
      this.pollRafId = null;
    }
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
